import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import { ButtonUI, CustomThemeProvider, ModalUI, usePortal } from 'sonata-react-components';
import { CampaignFamilyEnum } from 'sonata-static-storage/lib/schemas/campaign/campaign.schema';

import { MediaChannelSelector } from '../MediaChannelSelector/MediaChannelSelector';
import './MediaChannelSelectorModal.scss';

type State = {
  selectedChannel: CampaignFamilyEnum;
};

type Props = {
  id: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (newValue: CampaignFamilyEnum) => void;
  options: CampaignFamilyEnum[];
  value: CampaignFamilyEnum;
};

export const MediaChannelSelectorModal: React.VFC<Props> = ({ id, isOpen, onClose, onSubmit, options, value }) => {
  const targetBody = usePortal('MediaChannelSelectorModal');
  const modalRef = useRef<any>(null);
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (isOpen) {
      modalRef?.current?.openModal?.();
    } else {
      modalRef?.current?.closeModal?.();
    }
  }, [isOpen]);

  const [{ selectedChannel }, setState] = useState<State>({
    selectedChannel: value
  });

  const handleSelectorChange = (newValue: CampaignFamilyEnum) => {
    setState(prevState => ({
      ...prevState,
      selectedChannel: newValue
    }));
  };

  return ReactDOM.createPortal(
    <CustomThemeProvider themeContrast="light">
      <ModalUI
        className="mediaChannelSelectorModal"
        closeModalCallback={onClose}
        id={id}
        key={id}
        plainModal
        ref={modalRef}
        title=""
        width="770px"
      >
        <div className="mediaChannelSelectorModal__content">
          <MediaChannelSelector
            className="mediaChannelSelectorModal__selector"
            id={`mediaChannelSelector-${id}`}
            onChange={handleSelectorChange}
            options={options}
            subTitle={formatMessage({
              defaultMessage: 'The IO configuration may vary depending on the selected Media Channel.',
              id: 'mediaChannelSelection.modal.subtitle'
            })}
            title={formatMessage({
              defaultMessage: 'Select the Media Channel for the Insertion Order',
              id: 'mediaChannelSelection.modal.title'
            })}
            value={selectedChannel}
          />
          <div className="mediaChannelSelectorModal__buttonsContainer">
            <ButtonUI
              className="mediaChannelSelectorModal__cancelButton"
              id={`cancelButton-${id}`}
              onClick={onClose}
              shape="squareRounded"
              text={formatMessage({
                defaultMessage: 'Cancel',
                id: 'common.cancel'
              })}
              type="secondary"
            />
            <ButtonUI
              disabled={selectedChannel === value}
              id={`submitButton-${id}`}
              onClick={() => onSubmit(selectedChannel)}
              shape="squareRounded"
              text={formatMessage({
                defaultMessage: 'Save Changes',
                id: 'mediaChannelSelection.modal.button'
              })}
            />
          </div>
        </div>
      </ModalUI>
    </CustomThemeProvider>,
    targetBody
  );
};
